/**
 * The kinematic half of a tick — everything that rotates the aeroplane without
 * going through a force.
 *
 * `forces.ts` supplies an acceleration and `step.ts` integrates it; what happens
 * here is the four *commanded* rotations the original turns out to be built
 * from, none of which is a torque:
 *
 * 1. **AD-007h's produced turn rate** — a world-frame heading change applied to
 *    the velocity *and* the orientation together, so the nose carries around
 *    with the turn instead of opening a sideslip against it.
 * 2. **AD-007n's produced path-pitch rate** — the vertical-plane twin of it,
 *    applied as a rigid rotation of both about the path's own horizontal axis,
 *    so the angle of attack comes through unchanged. This is the elevator.
 * 3. **The slip relaxation**, whose two halves act on opposite things: AD-007j's
 *    path alignment rotates the **velocity only**, closing sideslip by swinging
 *    the flight path onto the nose, and AD-007n's α-relaxation rotates the
 *    **orientation only**, closing the angle of attack onto what the stick is
 *    asking for. Neither crosses over: an attitude term on the lateral axis
 *    would be the weathercock AD-007j deleted, and a velocity term on the pitch
 *    axis would be the covert path-curving pathway AD-007i deleted.
 * 4. **The rotary's gyroscopic precession** (AD-007m) — a yaw rate proportional
 *    to the *commanded* body pitch rate, orientation only, on the eight
 *    airframes the data gives a rotary torque pair.
 *
 * Split out of `step.ts` when AD-007n's elevator pushed that file past the
 * 400-line gate, on the same per-subject pattern `produced-turn.ts` came out on.
 * Nothing moved but the code.
 */

import { postStallFactor } from "./aero.ts";
import {
  rotaryPrecessionYawRadPerSec,
  slipRelaxationRates,
  type SlipRelaxationRates,
} from "./controls.ts";
import { boundedAlphaRelaxRadPerSec } from "./elevator-path.ts";
import type { ForceOutput } from "./forces.ts";
import {
  addVec3,
  advanceEulerAttitude,
  applyWorldPitch,
  applyWorldYaw,
  BODY_RIGHT,
  integrateAttitude,
  pathLateralAxis,
  pitchAboutWorldAxis,
  rotateVec3,
  scaleVec3,
  yawAboutBodyUp,
  yawAboutWorldUp,
  type BodyRates,
  type Quat,
  type Vec3,
} from "./math3d.ts";
import type { SimState } from "./state.ts";
import type { SubsystemFlags } from "./tiers.ts";

export interface KinematicsInput {
  readonly sim: SimState;
  readonly flags: SubsystemFlags;
  readonly forces: ForceOutput;
  /** The rates the *attitude* integrates — the pilot's, with the elevator's share already removed (AD-007n). */
  readonly rates: BodyRates;
  /** The pilot's commanded body pitch rate — precession's input, and deliberately not the attitude's (AD-007n). */
  readonly commandedPitchRate: number;
  readonly dt: number;
}

export interface KinematicsResult {
  readonly velocity: Vec3;
  readonly orientation: Quat;
}

/**
 * AD-007j's path alignment and AD-007n's α-relaxation, at this tick's air data.
 *
 * The two halves act on opposite things and both are gated by the same wing:
 * a departed one does not weathercock the nose onto the flight path any more
 * than it produces a turn (AD-007h's own `postStallFactor` rule, applied to the
 * mechanisms that share the curve). Without that gate the ungated relaxation
 * would pull a spinning aeroplane's angle of attack back below the break and end
 * the autorotation the spin model exists to hold. Neither does an aeroplane on
 * its wheels, where the elevator is back to levering the airframe about its
 * undercarriage and the attitude is the ground's business rather than the
 * relative wind's.
 */
function relaxationFor(input: KinematicsInput): SlipRelaxationRates {
  const { sim, forces } = input;
  const raw = slipRelaxationRates({
    tunables: sim.config.tunables,
    alphaRad: forces.airData.alphaRad,
    betaRad: forces.airData.betaRad,
    airspeedMps: forces.airData.airspeedMps,
    referenceAlphaRad: forces.referenceAlphaRad,
    stallFactor: sim.gear.onGround ? 0 : postStallFactor(sim.config.spec.aero, forces.airData.alphaRad),
  });
  // AD-007p: the elevator's two pathways rotate one nose, and §8.1's 22.1 °/s
  // was timed on the nose. `boundedAlphaRelaxRadPerSec`'s own note has the four
  // traces and why the path term is the one that keeps the ceiling.
  return {
    ...raw,
    pitchRadPerSec: boundedAlphaRelaxRadPerSec(
      raw.pitchRadPerSec,
      forces.pathPitchRateRadPerSec,
      sim.config.spec.control.pitchRateMaxRadPerSec,
    ),
  };
}

export function applyKinematics(input: KinematicsInput): KinematicsResult {
  const { sim, forces, rates, commandedPitchRate } = input;
  const ctx = input;
  const accelerated = addVec3(sim.velocity, scaleVec3(forces.acceleration, ctx.dt));
  // AD-007h: the produced turn rate is a kinematic world-frame heading change,
  // not a body rate, so it is applied here — after the body-rate attitude
  // integration and the force-based velocity update — to both the velocity
  // and the orientation together, so the nose carries around with the flight
  // path instead of opening a sideslip against it.
  const headingDeltaRad = forces.headingRateRadPerSec * ctx.dt;
  const relax = relaxationFor(input);
  // AD-007m: a rotary's engine is its own flywheel, so pitching the airframe
  // precesses it into yaw — measured at r = 0.36·q on the Camel, reversing with
  // the stick, absent on the S.E.5a. Orientation only, and in this slot rather
  // than in `commandedRates` for the same three reasons the relaxations are
  // here. It rides AD-007h's `applyWorldYaw` rather than the body-rate
  // integration, because it must leave the bank alone: see the function's own
  // note for the body-axis form that was tried and what its `tan θ` coupling did
  // to a full-back pull.
  const precessionYaw = rotaryPrecessionYawRadPerSec({
    torque: sim.config.spec.torque,
    flags: ctx.flags,
    tunables: sim.config.tunables,
    pitchRateRadPerSec: commandedPitchRate,
  });
  // AD-007n change 3: the elevator's own kinematic step, and the vertical-plane
  // twin of AD-007h's. The produced path-pitch rate rotates the velocity **and**
  // the orientation rigidly about the same world-horizontal axis, so the nose
  // goes round with the flight path and the angle of attack (and sideslip) come
  // through unchanged — the original's own behaviour, α between −1.4° and +5°
  // through every manoeuvre it was measured in. Zero with the stick centred.
  const turned = yawAboutBodyUp(
    yawAboutWorldUp(accelerated, headingDeltaRad),
    sim.orientation,
    relax.pathAlignRadPerSec * ctx.dt,
  );
  const pathPitchDeltaRad = sim.gear.onGround ? 0 : forces.pathPitchRateRadPerSec * ctx.dt;
  const pathAxis = pathLateralAxis(turned, rotateVec3(sim.orientation, BODY_RIGHT));
  return {
    velocity: pitchAboutWorldAxis(turned, pathAxis, pathPitchDeltaRad),
    // AD-007n change 1: three decoupled Euler axes, not a rigid-body quaternion
    // integration. `advanceEulerAttitude`'s own note has the measurement; the
    // short version is that the quaternion form carries `φ̇ = q·sin φ·tan θ` with
    // it, so the elevator rolled the aeroplane — 26° to 87° of bank with the
    // aileron released, where `RB.EXE` freezes it. AD-007m found the same
    // coupling in the precession term and routed that one term around it; this
    // makes it structural, for all three axes at once.
    // The α-relaxation keeps its **body-axis** rotation (`integrateAttitude`),
    // and that is deliberate rather than an oversight in change 1. Its job is to
    // close the angle of attack, which is a body-frame angle in the nose's own
    // pitch plane, so the rotation that closes it is the one about the span
    // axis. Routed through the Euler pitch instead it stops closing α at all
    // once the aeroplane is banked — at 70° the world's pitch plane is nearly
    // the aeroplane's yaw plane, so the term manufactures sideslip, AD-007j's
    // path alignment then swings the flight path down onto the nose, and the two
    // wind each other into a 25 °/s dive out of a manoeuvre the original flies at
    // −3.7 °/s. The pilot's *commanded* rates are the ones the original measures
    // as decoupled Euler axes (§8.1 finding 7); an aerodynamic restoring
    // rotation is a real rigid-body rotation and carries real rigid-body
    // kinematics, which at the sizes this term runs at is a fraction of a degree
    // of bank.
    orientation: applyWorldPitch(
      integrateAttitude(
        applyWorldYaw(
          advanceEulerAttitude(sim.orientation, rates, ctx.dt),
          headingDeltaRad + precessionYaw * ctx.dt,
        ),
        { roll: 0, pitch: relax.pitchRadPerSec, yaw: 0 },
        ctx.dt,
      ),
      pathAxis,
      pathPitchDeltaRad,
    ),
  };
}
